import DOMPurify from 'isomorphic-dompurify'
import { GetServerSideProps, GetStaticPaths } from 'next'
import { NextSeo } from 'next-seo'

import CallToActionSection from '@/components/call-to-action-section'
import Footer from '@/components/footer'
import { getformattedDate } from '@/helper/date'
import { BLOG_POST_QUERY, graphcms, IBlog } from '@/lib/hygraph'
import ContactUsSection from '@/modules/blog/contact-us-section'

type pageProps = {
  blog: IBlog
}

const Blog = (props: pageProps) => {
  const { blog } = props

  if (!blog) {
    return <></>
  }
  const sanitizedHTML = DOMPurify.sanitize(blog.content.html)

  return (
    <>
      <NextSeo
        openGraph={{
          images: [
            {
              secureUrl: blog.coverPhoto.url,
              width: 1200,
              height: 675,
              url: blog.coverPhoto.url,
              alt: blog.coverPhoto.fileName,
            },
          ],
        }}
        title={blog.metaTitle}
        description={blog.metaDescription}
        canonical={`${
          process.env.NEXT_PUBLIC_BASE_URL || 'https://themindsite.com'
        }/blog-posts/${blog.slug}`}
      />
      <div className="mx-auto max-w-container">
        <div className="mt-32 flex flex-col items-center justify-center px-4 sm:px-12 md:px-20 lg:block lg:px-40">
          <img
            alt={blog.coverPhoto.fileName}
            className="m-auto rounded-2xl px-2 lg:p-0 max-h-[60vh] object-cover w-full"
            src={blog.coverPhoto.url}
          />
          <div className="flex w-full flex-col items-start justify-start gap-y-3 pt-4 lg:pt-10">
            <div className="inline-flex items-start justify-start gap-2">
              <div className="flex items-start justify-start gap-2">
                <div className="text-lg font-medium text-zinc-400 md:text-xl lg:text-2xl">
                  {getformattedDate(blog.createdAt)}
                </div>
              </div>
              <div className="h-full border-r-2 border-r-zinc-400"></div>
              <div className="text-lg font-medium text-zinc-400 md:text-xl lg:text-2xl">
                {blog.readingTime} mins read
              </div>
            </div>
            <h1 className="w-full text-xl font-bold text-zinc-700 md:text-3xl lg:block lg:text-5xl/snug">
              {blog.title}
            </h1>
          </div>
          <section
            id="blog-section"
            className="relative flex max-w-container flex-col justify-center gap-x-24 py-4 lg:flex-row lg:py-14"
          >
            <div
              className="prose prose-h1:text-3xl prose-h1:text-zinc-700 prose-h2:text-zinc-700 prose-h3:text-2xl prose-h3:font-semibold prose-h3:text-zinc-700 prose-p:w-full prose-p:text-lg prose-p:font-light prose-p:text-zinc-500 prose-a:font-bold prose-a:no-underline prose-strong:font-bold prose-strong:text-zinc-600 prose-strong:no-underline prose-li:text-xl prose-li:font-light prose-li:text-zinc-500 prose-img:rounded-xl md:max-w-full md:prose-h3:text-2xl md:prose-p:text-xl/8"
              dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
            />
            <div className="sticky top-0 flex justify-center py-4 pt-20 lg:h-screen lg:w-3/5">
              <ContactUsSection />
            </div>
          </section>
        </div>
      </div>
      <CallToActionSection
        title="Make your online sales more profitable with e-commerce analytics."
        description={[
          'Discover how our digital shelf analytics can give you a competitive edge.',
          'Request a demo today and take the first step toward e-commerce success.',
        ]}
      />
      <Footer />
    </>
  )
}

// export const getServerSideProps: GetServerSideProps<pageProps> = async ctx => {
//   const { res, params } = ctx

//   res.setHeader(
//     'Cache-Control',
//     'public, s-maxage=10, stale-while-revalidate=600'
//   )

//   const blogPosts = (await graphcms
//     .request(BLOG_POST_QUERY)
//     .then((res: any) => res.blogPosts)) as IBlog[]

//   const blog = blogPosts.find(j => j.slug === params?.slug)

//   if (!blog) {
//     return {
//       props: {},
//       notFound: true,
//     }
//   }

//   return {
//     props: {
//       blog,
//     },
//   }
// }

export const getStaticPaths: GetStaticPaths = async () => {
  const blogPosts = (await graphcms
    .request(BLOG_POST_QUERY)
    .then((res: any) => res.blogPosts)) as IBlog[]

  const paths = blogPosts.map(blog => ({
    params: { slug: blog.slug },
  }))

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetServerSideProps<pageProps> = async ctx => {
  const { params } = ctx

  const blogPosts = (await graphcms
    .request(BLOG_POST_QUERY)
    .then((res: any) => res.blogPosts)) as IBlog[]

  const blog = blogPosts.find(j => j.slug === params?.slug)

  if (!blog) {
    return {
      props: {},
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      blog,
    },
    revalidate: 60,
  }
}

export default Blog
